import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitch from '../layout/LanguageSwitch';

const BotNotPresentPage = () => {
    const { serverId } = useParams();
    const { t } = useLanguage();

    // Récupérer le nom du serveur depuis l'URL ou localStorage si disponible
    const getServerName = () => {
        try {
            const token = localStorage.getItem('DISCORD_BOT_TOKEN');
            if (token) {
                const decoded = JSON.parse(atob(token.split('.')[1]));
                const guild = decoded.adminGuilds?.find(g => g.id === serverId);
                return guild?.name || 'Serveur Discord';
            }
        } catch (error) {
            console.error('Erreur lors de la récupération du nom du serveur:', error);
        }
        return 'Serveur Discord';
    };

    const serverName = getServerName();

    return (
        <div style={styles.container}>
            {/* Header */}
            <header style={styles.header}>
                <div style={styles.headerContent}>
                    <Link to="/welcome" style={styles.backButton}>
                        ⬅️ {t('navigation.backToServerList')}
                    </Link>
                    <LanguageSwitch />
                </div>
            </header>

            {/* Contenu principal */}
            <div style={styles.content}>
                <div style={styles.mainCard}>
                    {/* Icône et titre */}
                    <div style={styles.iconSection}>
                        <div style={styles.botIcon}>🤖</div>
                        <div style={styles.warningIcon}>⚠️</div>
                    </div>                    <h1 style={styles.title}>
                        {t('botNotPresent.title', 'Bot non présent sur {serverName}').replace('{serverName}', serverName)}
                    </h1>

                    <p style={styles.subtitle}>
                        {t('botNotPresent.subtitle', 'Le bot de gestion n\'est pas installé sur ce serveur Discord.')}
                    </p>

                    {/* Section d'explication */}
                    <div style={styles.explanationSection}>
                        <h2 style={styles.sectionTitle}>{t('botNotPresent.whyTitle', 'Pourquoi cette page ?')}</h2>
                        <div style={styles.reasonsList}>
                            <div style={styles.reasonItem}>
                                <span style={styles.reasonIcon}>🚫</span>
                                <div>
                                    <strong>{t('botNotPresent.reasons.notInstalled.title', 'Bot non installé')}</strong>
                                    <p>{t('botNotPresent.reasons.notInstalled.description', 'Le bot n\'a pas été ajouté à ce serveur Discord')}</p>
                                </div>
                            </div>
                            <div style={styles.reasonItem}>
                                <span style={styles.reasonIcon}>🔒</span>
                                <div>
                                    <strong>{t('botNotPresent.reasons.insufficientPermissions.title', 'Permissions insuffisantes')}</strong>
                                    <p>{t('botNotPresent.reasons.insufficientPermissions.description', 'Le bot n\'a pas les permissions nécessaires sur ce serveur')}</p>
                                </div>
                            </div>
                            <div style={styles.reasonItem}>
                                <span style={styles.reasonIcon}>📤</span>
                                <div>
                                    <strong>{t('botNotPresent.reasons.botRemoved.title', 'Bot retiré')}</strong>
                                    <p>{t('botNotPresent.reasons.botRemoved.description', 'Le bot a été supprimé du serveur après votre dernière connexion')}</p>
                                </div>
                            </div>
                        </div>
                    </div>                    {/* Section d'actions */}
                    <div style={styles.actionsSection}>
                        <h2 style={styles.sectionTitle}>{t('botNotPresent.whatToDoTitle', 'Que faire ?')}</h2>
                        
                        <div style={styles.actionGrid}>
                            <div style={styles.actionCard}>
                                <span style={styles.actionIcon}>➕</span>
                                <h3>{t('botNotPresent.actions.inviteBot.title', 'Ajouter le bot')}</h3>
                                <p>{t('botNotPresent.actions.inviteBot.description', 'Invitez le bot sur votre serveur Discord avec les permissions appropriées')}</p>
                                <button style={styles.actionButton} onClick={() => {
                                    window.open(`https://discord.com/api/oauth2/authorize?client_id=${process.env.REACT_APP_DISCORD_CLIENT_ID}&permissions=8&scope=bot&guild_id=${serverId}`, '_blank');
                                }}>
                                    {t('botNotPresent.actions.inviteBot.button', 'Inviter le bot')}
                                </button>
                            </div>                            <div style={styles.actionCard}>
                                <span style={styles.actionIcon}>👥</span>
                                <h3>{t('botNotPresent.actions.contactAdmin.title', 'Contacter un administrateur')}</h3>
                                <p>{t('botNotPresent.actions.contactAdmin.description', 'Demandez à un administrateur de l\'aide.')}</p>
                                <button style={styles.actionButton} onClick={() => {
                                    window.open(`https://discord.com/channels/${serverId}`, '_blank');
                                }}>
                                    {t('botNotPresent.actions.contactAdmin.button', 'Ouvrir Discord')}
                                </button>
                            </div>

                            <div style={styles.actionCard}>
                                <span style={styles.actionIcon}>🔄</span>
                                <h3>{t('botNotPresent.actions.checkPermissions.title', 'Vérifier les permissions')}</h3>
                                <p>{t('botNotPresent.actions.checkPermissions.description', 'Si le bot vient d\'être ajouté, actualisez votre session')}</p>
                                <button style={styles.actionButton} onClick={() => {
                                    localStorage.removeItem('DISCORD_BOT_TOKEN');
                                    window.location.href = '/';
                                }}>
                                    {t('botNotPresent.tryAgain', 'Actualiser')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#121212',
        color: '#f8f1e4'
    },
    header: {
        backgroundColor: '#1a1a1a',
        borderBottom: '2px solid #333',
        padding: '20px',
        position: 'sticky',
        top: 0,
        zIndex: 1000
    },
    headerContent: {
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: 'rgba(79, 84, 92, 0.3)',
        color: '#b0b0b0',
        textDecoration: 'none',
        borderRadius: '8px',
        border: '1px solid rgba(79, 84, 92, 0.5)',
        transition: 'all 0.3s ease',
        fontSize: '14px',
        fontWeight: '500'
    },
    content: {
        padding: '40px 20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: 'calc(100vh - 100px)'
    },
    mainCard: {
        maxWidth: '800px',
        width: '100%',
        backgroundColor: '#1a1a1a',
        borderRadius: '16px',
        border: '2px solid #333',
        padding: '40px',
        textAlign: 'center'
    },
    iconSection: {
        position: 'relative',
        display: 'inline-block',
        marginBottom: '30px'
    },
    botIcon: {
        fontSize: '80px',
        filter: 'grayscale(100%)',
        opacity: 0.6
    },
    warningIcon: {
        position: 'absolute',
        top: '0',
        right: '-10px',
        fontSize: '30px',
        backgroundColor: '#1a1a1a',
        borderRadius: '50%',
        padding: '5px'
    },
    title: {
        fontSize: '32px',
        fontWeight: 'bold',
        color: '#f8f1e4',
        marginBottom: '15px'
    },
    subtitle: {
        fontSize: '18px',
        color: '#ccc',
        marginBottom: '40px'
    },
    explanationSection: {
        marginBottom: '40px',
        textAlign: 'left'
    },
    sectionTitle: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#f8f1e4',
        marginBottom: '20px',
        textAlign: 'center'
    },
    reasonsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },
    reasonItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '15px',
        padding: '20px',
        backgroundColor: '#222',
        borderRadius: '12px',
        border: '1px solid #333'
    },
    reasonIcon: {
        fontSize: '24px',
        marginTop: '5px'
    },    actionsSection: {
        marginBottom: '40px'
    },
    actionGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginTop: '20px'
    },
    actionCard: {
        backgroundColor: '#222',
        borderRadius: '12px',
        border: '1px solid #333',
        padding: '25px',
        textAlign: 'center',
        transition: 'all 0.3s ease'
    },
    actionIcon: {
        fontSize: '36px',
        display: 'block',
        marginBottom: '15px'
    },
    actionButton: {
        backgroundColor: '#4CAF50',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '15px'
    }
};

export default BotNotPresentPage;
